/**
 * Centralized Authentication API Service
 * All auth operations (sign in, sign out, profile) go through this service
 * Automatically routes to correct endpoints based on role
 */

import { apiClient } from './client'
import {
  SignInRequest,
  UserSignInResponse,
  AdminSignInResponse,
  UserMeResponse,
  AdminMeResponse,
  SignOutResponse,
  UpdateUserProfileRequest,
  UpdateAdminPasswordRequest,
} from './types'
import { UserRole } from '../../types/auth'

export const authApi = {
  /**
   * Sign in as user or admin
   * Automatically calls correct endpoint based on role
   */
  signIn: async (role: UserRole, email: string, password: string) => {
    const credentials: SignInRequest = { email, password }
    
    if (role === 'admin') {
      return apiClient.post<AdminSignInResponse>(
        '/auth/admins/sign_in',
        credentials,
        { skipAuth: true, skipRoleRouting: true }
      )
    } else {
      return apiClient.post<UserSignInResponse>(
        '/auth/users/sign_in',
        credentials,
        { skipAuth: true, skipRoleRouting: true }
      )
    }
  },

  /**
   * Get current user or admin profile
   * Automatically calls correct endpoint based on role
   */
  getCurrentProfile: async (role: UserRole) => {
    if (role === 'admin') {
      return apiClient.get<AdminMeResponse>('/auth/admins/me', { skipRoleRouting: true })
    } else {
      return apiClient.get<UserMeResponse>('/auth/users/me', { skipRoleRouting: true })
    }
  },

  /**
   * Sign out current user or admin
   * Automatically calls correct endpoint based on role
   */
  signOut: async (role: UserRole) => {
    if (role === 'admin') {
      return apiClient.delete<SignOutResponse>('/auth/admins/sign_out', { skipRoleRouting: true })
    } else {
      return apiClient.delete<SignOutResponse>('/auth/users/sign_out', { skipRoleRouting: true })
    }
  },

  /**
   * Update user profile
   */
  updateUserProfile: async (data: UpdateUserProfileRequest) => {
    return apiClient.patch<UserMeResponse>('/users/me', data)
  },

  /**
   * Update admin password
   */
  updateAdminPassword: async (data: UpdateAdminPasswordRequest) => {
    return apiClient.patch<AdminMeResponse>('/administrator/admins/me', data)
  },
}

